import { useEffect, useMemo } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';
import { ErrorPage } from '@zagru/design-system';
import { createCorrelationId, isZagruProblem, useT } from '@zagru/platform';
import { ErrorFallback } from '@/app/ErrorBoundary';
import { versionStore } from '@/app/state/appStores';
import { NotFoundPage, renderErrorPageLink } from './NotFoundPage';

/** A lazily loaded route chunk that no longer exists: the app was updated while this tab was open. */
function isStaleChunk(error: unknown): boolean {
  const message = error instanceof Error ? error.message : '';
  return /dynamically imported module|Importing a module script failed|Failed to fetch dynamically/i.test(message);
}

function StaleChunkPage() {
  const t = useT();
  const markNewVersion = versionStore((s) => s.markNewVersion);
  useEffect(() => markNewVersion(), [markNewVersion]);
  return (
    <ErrorPage
      variant="maintenance"
      icon="refresh"
      title={t('shell.error.staleChunk.title')}
      description={t('shell.error.staleChunk.description')}
      primaryAction={{ label: t('shell.error.reload'), onClick: () => window.location.reload() }}
      renderLink={renderErrorPageLink}
    />
  );
}

/** Route-level `errorElement`: 404 renders the not-found page; everything else a calm, diagnostic-safe fallback. */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const t = useT();
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;
  if (isStaleChunk(error)) return <StaleChunkPage />;
  if (isRouteErrorResponse(error) && error.status === 403) {
    return (
      <ErrorPage
        variant="403"
        title={t('shell.error.forbidden.title')}
        description={t('shell.error.forbidden.description')}
        primaryAction={{ label: t('shell.error.goHome'), href: '/today' }}
        secondaryAction={{ label: t('shell.error.goBack'), onClick: () => navigate(-1) }}
        renderLink={renderErrorPageLink}
      />
    );
  }
  // API problems keep the problem-aware fallback (their own copy, retry and diagnostic id).
  if (isZagruProblem(error)) return <ErrorFallback error={error} onRetry={() => navigate(0)} />;
  return <RenderFailurePage error={error} />;
}

/** An unexpected render failure: a calm 500 with a diagnostic reference that matches the console record. */
function RenderFailurePage({ error }: { error: unknown }) {
  const navigate = useNavigate();
  const t = useT();
  const referenceId = useMemo(() => createCorrelationId(), []);
  useEffect(() => {
    console.error(`[zagru] route failure (${referenceId})`, error);
  }, [error, referenceId]);
  return (
    <ErrorPage
      variant="500"
      title={t('shell.error.server.title')}
      description={t('shell.error.server.description')}
      referenceId={referenceId}
      referenceLabel={t('shell.error.reference')}
      technicalLabel={t('failure.technicalDetails')}
      primaryAction={{ label: t('failure.tryAgain'), onClick: () => navigate(0) }}
      secondaryAction={{ label: t('shell.error.goHome'), href: '/today' }}
      technical={isRouteErrorResponse(error) ? <code className="code">{`${error.status} ${error.statusText}`}</code> : undefined}
      renderLink={renderErrorPageLink}
    />
  );
}
